import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';

import { DprCard, DprCards } from '@dpr/models';
import { DprApprover, DprDetails, DprOnlineApproval } from '@shared/models/dpr';
import { DprBasePersistenceService } from '.';
import { convertToDateWithoutTimeZoneSuffix } from '@shared/utils';
import { GenericHttpClientService } from '@shared/services';
import { HttpDprContext } from '@shared/enums';

@Injectable()
export class DprRemotePersistenceService extends DprBasePersistenceService {
  constructor(private genericHttpClientService: GenericHttpClientService) {
    super();
  }

  getDprCards(filters: HttpParams): Observable<DprCards> {
    return this.genericHttpClientService.get<DprCards>({
      context: HttpDprContext.GetDprCards,
      params: filters,
    });
  }

  lockDpr(
    dprDetailId: number | string,
    subprojectId: number,
    lockDate: string
  ): Observable<DprDetails> {
    const model = {
      dprDetailId,
      subprojectId,
      dateTime: lockDate,
    };
    return this.genericHttpClientService.post<DprDetails>({
      context: HttpDprContext.LockDpr,
      body: model,
    });
  }

  unlockDpr(dprDetailId: number | string): Observable<number | string> {
    return this.genericHttpClientService.get<number>({
      context: HttpDprContext.UnlockDpr,
      args: {
        dprDetailId,
      },
    });
  }

  createDpr(subprojectId: number, date: Date): Observable<DprCard> {
    const model = {
      subprojectId,
      date: date ? convertToDateWithoutTimeZoneSuffix(date) : null,
    };
    return this.genericHttpClientService.post<DprCard>({
      context: HttpDprContext.CreateDpr,
      body: model,
    });
  }

  createDprApprover(
    model: DprApprover,
    subprojectId: number
  ): Observable<DprApprover> {
    return this.genericHttpClientService.post<DprApprover>({
      context: HttpDprContext.CreateDprApprover,
      body: {
        subprojectId,
        name: model.name,
        dprDetailId: model.dprDetailId,
        approverId: model.approverId,
      },
    });
  }

  updateDprApprover(
    model: DprApprover,
    subprojectId: number
  ): Observable<DprApprover> {
    return this.genericHttpClientService.post<DprApprover>({
      context: HttpDprContext.UpdateDprApprover,
      body: {
        subprojectId,
        id: model.id,
        name: model.name,
      },
    });
  }

  deleteDprApprover(
    dprApproverId: number | string,
    subprojectId: number,
    _dprDetailId: number | string
  ): Observable<void> {
    return this.genericHttpClientService.post<void>({
      context: HttpDprContext.DeleteDprApprover,
      body: {
        id: dprApproverId,
        subprojectId,
      },
    });
  }

  syncDprOnlineApproval(
    model: DprOnlineApproval,
    subprojectId: number
  ): Observable<{
    dprOnlineApproval: DprOnlineApproval;
    dprDetail: DprDetails;
  }> {
    return this.genericHttpClientService.post<
      {
        dprOnlineApproval: DprOnlineApproval;
        dprDetail: DprDetails;
      },
      object
    >({
      context: HttpDprContext.SyncDprOnlineApproval,
      body: {
        subprojectId,
        dprOnlineApproval: model,
      },
    });
  }
}
